// ── Normalizar transcript de voz ───────────────────────────────────────────
// Deepgram no añade signos de interrogación. Si el texto empieza con una
// palabra interrogativa, lo convertimos en pregunta explícita para que
// el adversario no lo malinterprete como afirmación.
const INTERROGATIVAS = [
  "qué", "que", "cómo", "como", "cuándo", "cuando", "dónde", "donde",
  "por qué", "por que", "quién", "quien", "cuál", "cual", "cuánto",
  "cuanto", "podría", "podria", "puede", "pudo", "vio", "vio usted",
  "estuvo", "estaba", "conoce", "conocía", "recuerda", "recuerdas",
  "tiene", "tenía", "sabe", "sabía", "ha", "había", "fue", "es",
];

export function normalizeTranscript(text) {
  if (!text) return text;
  const trimmed = text.trim();
  // Si ya termina en signo de interrogación, devolver tal cual
  if (trimmed.endsWith("?")) return trimmed;
  const lower = trimmed.toLowerCase();
  // Si empieza con palabra interrogativa, añadir signo de interrogación
  const esInterrogativa = INTERROGATIVAS.some(w => lower.startsWith(w + " ") || lower === w);
  if (esInterrogativa) return trimmed + "?";
  return trimmed;
}
export function stripMd(text) {
  return (text || "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")  // bold
    .replace(/\*([^*]+)\*/g, "$1")       // italic
    .replace(/^#{1,6}\s+/gm, "")         // headings
    .replace(/^[-*]\s+/gm, "")           // bullet points
    .replace(/`([^`]+)`/g, "$1")         // inline code
    .trim();
}

// ── Case bias text by role + difficulty ───────────────────────────────────
export function getCaseBias(userRole, difficulty) {
  const map = {
    fiscal: {
      basico:     "IMPORTANTE: el caso debe ser claramente favorable a la Fiscalía. La evidencia de cargo es sólida y consistente. Los testigos de la Fiscalía son creíbles y sus testimonios coinciden. Hay pocos elementos que la defensa pueda explotar.",
      intermedio: "El caso debe ser moderadamente favorable a la Fiscalía, con evidencia razonable pero con algunos puntos ambiguos que la defensa puede intentar explotar.",
      avanzado:   "El caso debe ser equilibrado, con evidencia mixta. La Fiscalía tiene argumentos sólidos pero la defensa tiene contraargumentos igualmente fuertes. Requiere habilidad procesal.",
    },
    defensor: {
      basico:     "IMPORTANTE: el caso debe tener debilidades claras en la evidencia fiscal que un defensor hábil puede explotar: contradicciones entre testigos, evidencia circunstancial, procedimientos policiales cuestionables, dudas razonables.",
      intermedio: "El caso debe tener algunos elementos que generan duda razonable para la defensa, aunque la evidencia fiscal es moderada.",
      avanzado:   "El caso debe ser equilibrado o incluso difícil para la defensa, con evidencia fiscal bastante sólida que requiere estrategia para rebatir.",
    },
  };
  return map[userRole]?.[difficulty] || "";
}

// ── Document HTML generators ───────────────────────────────────────────────
export function generateDocHTML(title, tipo, content, caseData) {
  const date = new Date().toLocaleDateString("es-EC", { day:"2-digit", month:"long", year:"numeric" });
  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>${tipo} — ${title}</title>
<style>
  body{font-family:'Times New Roman',serif;max-width:820px;margin:0 auto;padding:48px 40px;color:#111;line-height:1.85;font-size:14px}
  .header{border:2px solid #111;padding:20px;margin-bottom:28px;text-align:center}
  h1{font-size:17px;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:13px;text-align:left}
  .content{white-space:pre-wrap;line-height:1.9}
  .print-btn{position:fixed;top:24px;right:24px;padding:11px 22px;background:#0F0C08;color:#fff;border:none;border-radius:6px;cursor:pointer;font-family:monospace;font-weight:700;font-size:13px}
  @media print{.print-btn{display:none}}
</style></head><body>
<button class="print-btn" onclick="window.print()">⬇ Guardar como PDF</button>
<div class="header">
  <h1>REPÚBLICA DEL ECUADOR<br>${tipo.toUpperCase()}</h1>
  <div class="meta">
    <div><strong>Causa N°:</strong> ${caseData.numero_causa || ""}</div>
    <div><strong>Fecha:</strong> ${date}</div>
    <div><strong>Procesado/a:</strong> ${caseData.procesado}</div>
    <div><strong>Víctima:</strong> ${caseData.victima}</div>
  </div>
</div>
<div class="content">${content}</div>
</body></html>`;
}

export function generateReportHTML(witness, content, caseData) {
  const tipo = witness.tipo === "policia" ? "Parte Policial" : "Informe Pericial";
  return generateDocHTML(witness.nombre, tipo, content, caseData);
}

export function openDoc(htmlContent) {
  const blob = new Blob([htmlContent], { type:"text/html;charset=utf-8" });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, "_blank");
  if (win) win.focus();
}
